import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { Navbar, Button } from 'flowbite-react';
import Login from '../screens/Login';
import Register from '../screens/Register';
import Apps from '../screens/Apps';
import Users from '../screens/Users';
import UserDetail from '../screens/UserDetail';
import Properties from '../screens/Properties';
import PropertyForm from '../screens/PropertyForm';
import PropertyStats from '../screens/PropertyStats';
import Broadcasts from '../screens/Broadcasts';
import AppStats from '../screens/AppStats';
import CreateApp from '../screens/CreateApp';
import Settings from '../screens/Settings';
import BotSettings from '../screens/BotSettings';
import Sidebar from '../components/Sidebar';
import Breadcrumbs from '../components/Breadcrumbs';
import Footer from '../components/Footer';
import { api } from '../api';
import { BASENAME } from '../config';
import ConfirmModal from '../components/ConfirmModal';

const isAuthenticated = () => Boolean(localStorage.getItem('dashboard_token'));

const Protected = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/login" replace />;
};

export default function App() {
  const location = useLocation();
  const match = location.pathname.match(/^\/([0-9a-fA-F-]{36})(?:\/|$)/);
  const currentAppUuid = match ? match[1] : null;
  const [currentApp, setCurrentApp] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  useEffect(() => {
    if (currentAppUuid && isAuthenticated()) {
      // Fetch app info from the apps list
      api.get('/apps')
        .then(data => {
          const app = data.items?.find(item => item.id === currentAppUuid);
          setCurrentApp(app || null);
        })
        .catch(() => setCurrentApp(null));
    } else {
      setCurrentApp(null);
    }
  }, [currentAppUuid]);

  // Close mobile drawer on route change
  useEffect(() => {
    if (mobileOpen) setMobileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const showSidebar = isAuthenticated() && location.pathname !== '/login' && location.pathname !== '/register';
  const showFooter = location.pathname !== '/login' && location.pathname !== '/register';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <ConfirmModal
        open={confirmLogout}
        title="Выйти из аккаунта?"
        message="Вы действительно хотите выйти из личного кабинета?"
        confirmText="Выйти"
        cancelText="Отмена"
        onClose={() => setConfirmLogout(false)}
        onConfirm={() => { setConfirmLogout(false); localStorage.removeItem('dashboard_token'); window.location.href = `${BASENAME || ''}/login`; }}
      />
      <Navbar fluid rounded className="border-b sticky top-0 z-50 bg-white">
        <Navbar.Brand as={Link} to="/" className="flex items-center">
          <span className="text-2xl mr-2">🏠</span>
          <span className="self-center whitespace-nowrap text-xl font-bold">
            <span className="text-blue-600">Real</span>
            <span className="text-gray-800">gram</span>
          </span>
          <span className="ml-2 text-sm text-gray-500 font-normal">Панель управления</span>
        </Navbar.Brand>
        {showSidebar && (
          <button
            className="md:hidden inline-flex items-center p-2 text-sm text-gray-500 rounded-lg hover:bg-gray-100 focus:outline-none"
            aria-controls="drawer-sidebar"
            aria-expanded={mobileOpen ? 'true' : 'false'}
            aria-label="Открыть меню"
            onClick={() => setMobileOpen((v) => !v)}
          >
            <svg className="w-6 h-6" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 5h14a1 1 0 100-2H3a1 1 0 000 2zm14 4H3a1 1 0 000 2h14a1 1 0 100-2zm0 6H3a1 1 0 000 2h14a1 1 0 100-2z" clipRule="evenodd" /></svg>
          </button>
        )}
        <div className="flex md:order-2 gap-2">
          {isAuthenticated() ? (
            <Button color="light" onClick={() => setConfirmLogout(true)}>Выйти</Button>
          ) : (
            <div className="flex gap-2">
              <Button as={Link} to="/login">Войти</Button>
              <Button color="light" as={Link} to="/register">Регистрация</Button>
            </div>
          )}
        </div>
      </Navbar>

      <div className="flex flex-1">
        {showSidebar && (
          <>
            <div className="hidden md:block w-64 bg-white border-r">
              <Sidebar currentApp={currentApp} />
            </div>
            {/* Mobile drawer */}
            <div className={`${mobileOpen ? '' : 'hidden'} md:hidden fixed inset-0 z-40`} role="dialog" aria-modal="true">
              <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)}></div>
              <div id="drawer-sidebar" className="absolute left-0 top-0 h-full w-72 bg-white shadow-lg">
                <div className="p-3 border-b flex items-center justify-between">
                  <div className="font-semibold">Навигация</div>
                  <button className="p-2 text-gray-500 hover:bg-gray-100 rounded" aria-label="Закрыть меню" onClick={() => setMobileOpen(false)}>✕</button>
                </div>
                <div className="p-2" onClick={(e) => {
                  const target = e.target;
                  if (target.closest('a')) setMobileOpen(false);
                }}>
                  <Sidebar currentApp={currentApp} />
                </div>
              </div>
            </div>
          </>
        )}

        <main className={`flex-1 p-4 md:p-6 ${showSidebar ? '' : 'container mx-auto'}`}>
          {showSidebar && <Breadcrumbs currentApp={currentApp} />}

          <Routes>
            <Route path="/" element={<Navigate to="/apps" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/apps" element={<Protected><Apps /></Protected>} />
            <Route path="/apps/new" element={<Protected><CreateApp /></Protected>} />
            <Route path="/:appUuid/dashboard" element={<Protected><AppStats /></Protected>} />
            <Route path="/:appUuid/users" element={<Protected><Users /></Protected>} />
            <Route path="/:appUuid/properties" element={<Protected><Properties /></Protected>} />
            <Route path="/:appUuid/properties/new" element={<Protected><PropertyForm /></Protected>} />
            <Route path="/:appUuid/properties/:propId/edit" element={<Protected><PropertyForm /></Protected>} />
            <Route path="/:appUuid/properties/:propId/stats" element={<Protected><PropertyStats /></Protected>} />
            <Route path="/:appUuid/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/:appUuid/bot" element={<Protected><BotSettings /></Protected>} />
            <Route path="/:appUuid/broadcasts" element={<Protected><Broadcasts /></Protected>} />
            <Route path="/users/:userId" element={<Protected><UserDetail /></Protected>} />
          </Routes>
        </main>
      </div>

      {showFooter && <Footer />}
    </div>
  );
}
